import { Component, ErrorInfo, ReactNode, ComponentType } from 'react';
import { perf } from '../utils/perfMetrics';
import { logger } from '../utils/logger';
import { useI18n } from '../i18n/I18nProvider';

type ErrorFallbackProps = {
  error: Error;
  resetError: () => void;
};

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ComponentType<ErrorFallbackProps>;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

function DefaultErrorFallback({ error, resetError }: ErrorFallbackProps) {
  const { t } = useI18n();
  return (
    <div className="p-6 text-center text-sm text-red-300 space-y-2" role="alert">
      <h2 className="text-lg font-semibold text-red-200">{t('error.title')}</h2>
      <p>{t('error.message')}</p>
      {error?.message && (
        <p className="text-xs text-red-200/80">{error.message}</p>
      )}
      <button
        type="button"
        className="inline-flex items-center gap-2 rounded-md bg-red-500 px-4 py-2 text-sm font-medium text-white hover:bg-red-400"
        onClick={resetError}
      >
        {t('error.refresh')}
      </button>
    </div>
  );
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    perf.inc('errors.boundary');
    logger.error('GAME_ERROR_BOUNDARY', { error, errorInfo });
    this.props.onError?.(error, errorInfo);
  }

  resetError = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError && this.state.error) {
      const Fallback = this.props.fallback || DefaultErrorFallback;
      return <Fallback error={this.state.error} resetError={this.resetError} />;
    }

    return this.props.children;
  }
}

export function GameErrorBoundary({ children, fallback }: { children: ReactNode; fallback?: ComponentType<ErrorFallbackProps> }) {
  const handleError = (error: Error) => {
    if (
      error.message?.includes('ONLINE')
      || error.message?.includes('online')
      || error.message?.includes('PARTYKIT')
    ) {
      setTimeout(() => {
        if (typeof window !== 'undefined') {
          window.location.reload();
        }
      }, 3000);
    }
  };
  return (
    <ErrorBoundary fallback={fallback} onError={handleError}>
      {children}
    </ErrorBoundary>
  );
}
